import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useSearch } from "../../../contexts/SearchContext";
import {
  countFlightPassengersFromRoomInfo,
  createEmptyFlightPassengerSlot,
  DEFAULT_FLIGHT_PASSENGER_NATIONALITY,
} from "../adapters/flightOfferAdapter";
import { FlightBookingMockupStepCardLayout } from "./StepCardLayout";
import FlightPassengersFormBody from "./FlightPassengersFormBody";
import {
  normalizePassengersNationality,
  shouldRequirePassportOnAgentFlightForm,
  validateFlightPassengerPax,
} from "./flightPassengersFormShared";
import { BookingFlowContext } from "./context/bookingFlowReactContext";

export default function PassengersStepCard({
  forceActive = false,
  compactInDetails = false,
  passengers: passengersProp,
  updatePassengers: updatePassengersProp,
  setPassengersStepValid: setPassengersStepValidProp,
  currentStep: currentStepProp,
}) {
  const { roomInfo } = useSearch();
  const bookingFlow = useContext(BookingFlowContext);
  const passengers = Array.isArray(passengersProp)
    ? passengersProp
    : Array.isArray(bookingFlow?.passengers)
      ? bookingFlow.passengers
      : [];
  const updatePassengers = updatePassengersProp || bookingFlow?.updatePassengers || (() => {});
  const setPassengersStepValid =
    setPassengersStepValidProp || bookingFlow?.setPassengersStepValid || (() => {});
  const currentStep = currentStepProp || bookingFlow?.currentStep || null;
  const [dateFieldErrors, setDateFieldErrors] = useState({});
  const isActiveStep = currentStep === "passengers" || forceActive;
  const requirePassport = shouldRequirePassportOnAgentFlightForm(
    bookingFlow?.selectedFlightOffer
  );

  const expected = useMemo(
    () => countFlightPassengersFromRoomInfo(roomInfo),
    [roomInfo]
  );

  useEffect(() => {
    if (!isActiveStep) return;
    if (passengers.length === expected) return;
    const next = Array.from({ length: expected }, (_, i) => {
      if (passengers[i]) {
        const p = { ...passengers[i], slotIndex: i };
        if (!String(p.nationality || "").trim()) {
          p.nationality = DEFAULT_FLIGHT_PASSENGER_NATIONALITY;
        }
        if (p.cell === undefined) p.cell = "";
        if (p.email === undefined) p.email = "";
        return p;
      }
      return createEmptyFlightPassengerSlot(i);
    });
    updatePassengers(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- סנכרון אורך מול חיפוש בלבד
  }, [isActiveStep, expected, passengers.length]);

  useEffect(() => {
    if (!isActiveStep) return;
    if (passengers.length !== expected || expected < 1) return;
    const { next, changed } = normalizePassengersNationality(passengers);
    if (changed) updatePassengers(next);
  }, [isActiveStep, expected, passengers, updatePassengers]);

  const allValid = useMemo(() => {
    if (passengers.length !== expected || expected < 1) return false;
    return passengers.every((p, i) =>
      validateFlightPassengerPax(p, i, { requirePassport })
    );
  }, [passengers, expected, requirePassport]);

  useLayoutEffect(() => {
    if (!isActiveStep) return;
    setPassengersStepValid(allValid);
    if (process.env.NODE_ENV !== "production" && currentStep === "passengers") {
      console.log("[FlightBookingMockup] passengers state", {
        expected,
        count: passengers.length,
        passengersStepValid: allValid,
      });
    }
  }, [allValid, isActiveStep, setPassengersStepValid, currentStep, expected, passengers.length]);

  const patch = useCallback(
    (index, field, value) => {
      const next = passengers.map((p, i) =>
        i === index ? { ...p, [field]: value } : p
      );
      updatePassengers(next);
    },
    [passengers, updatePassengers]
  );

  return (
    <FlightBookingMockupStepCardLayout
      variant="body"
      className={compactInDetails ? "fw-passengers-step-card--embedded" : ""}
    >
      <p className="fw-passengers-intro">
        {`${expected} נוסעים לפי הרכב החדרים בחיפוש · שדות חובה לכל נוסע · טלפון ודוא״ל חובה לנוסע הראשי`}
      </p>
      <FlightPassengersFormBody
        passengers={passengers}
        onPatch={patch}
        dateFieldErrors={dateFieldErrors}
        setDateFieldErrors={setDateFieldErrors}
        requirePassport={requirePassport}
      />
      <p
        className={
          allValid
            ? "fw-ui-pax-status fw-ui-pax-status--ok"
            : "fw-ui-pax-status"
        }
      >
        {allValid
          ? "כל השדות החובה מולאו — אפשר להמשיך לסיכום ההזמנה."
          : "מלאו את כל שדות החובה (כולל תאריכים בפורמט DD/MM/YYYY, טלפון ודוא״ל תקין לנוסע הראשי)."}
      </p>
    </FlightBookingMockupStepCardLayout>
  );
}
